import {
  Email as EmailIcon,
  Link as LinkIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState, type FormEvent } from "react";
import { useNotification } from "../hooks/useNotification";

interface EmailInputFormProps {
  onSubmit: (email: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  initialValue?: string;
}

export function EmailInputForm({
  onSubmit,
  isLoading = false,
  disabled = false,
  initialValue = "",
}: EmailInputFormProps) {
  const [email, setEmail] = useState(initialValue);
  const { showNotification } = useNotification();

  // Update email when initialValue changes (e.g., from URL query param)
  useEffect(() => {
    if (initialValue) {
      setEmail(initialValue);
    }
  }, [initialValue]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      onSubmit(email.trim());
    }
  };

  const handleCopyLink = async () => {
    if (!email.trim()) return;

    const url = new URL(window.location.origin);
    url.searchParams.set("email", email.trim());

    try {
      await navigator.clipboard.writeText(url.toString());
      showNotification("Link copied to clipboard", "success", 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const isSubmitDisabled = !email.trim() || isLoading || disabled;

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "flex-start" },
        }}
      >
        <TextField
          fullWidth
          label="Email Address"
          placeholder="Enter an email address to validate"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading || disabled}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: email.trim() && (
                <InputAdornment position="end">
                  <Tooltip title="Copy shareable link">
                    <IconButton
                      onClick={handleCopyLink}
                      edge="end"
                      size="small"
                      aria-label="Copy shareable link to clipboard"
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          }}
          aria-describedby="email-helper-text"
          sx={{ flexGrow: 1 }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitDisabled}
          endIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          sx={{
            minWidth: { xs: "100%", sm: 140 },
            height: 56,
          }}
        >
          {isLoading ? "Validating..." : "Validate"}
        </Button>
      </Box>
    </>
  );
}
